import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@freelancer-os/ui/src/components/ui/dialog';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Input } from '@freelancer-os/ui/src/components/ui/input';
import { Label } from '@freelancer-os/ui/src/components/ui/label';
import { Textarea } from '@freelancer-os/ui/src/components/ui/textarea';
import type { Invoice } from '@freelancer-os/shared-types';
import {
  useDraftInvoiceMessage,
  useDraftReminder,
  useSendInvoice,
  useSendReminder,
} from '../../../hooks/useInvoices';

type Mode = 'send' | 'reminder';

interface SendInvoiceDialogProps {
  invoice: Invoice;
  mode: Mode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SendInvoiceDialog({
  invoice,
  mode,
  open,
  onOpenChange,
}: SendInvoiceDialogProps) {
  const { t } = useTranslation('invoices');

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [populated, setPopulated] = useState(false);

  const draftInvoice = useDraftInvoiceMessage(invoice.id);
  const draftReminder = useDraftReminder(invoice.id);
  const sendInvoice = useSendInvoice(invoice.id);
  const sendReminder = useSendReminder(invoice.id);

  const draftMutation = mode === 'send' ? draftInvoice : draftReminder;
  const sendMutation = mode === 'send' ? sendInvoice : sendReminder;

  useEffect(() => {
    if (!open) {
      setTo('');
      setSubject('');
      setBody('');
      setPopulated(false);
      return;
    }
    setPopulated(false);
    draftMutation.mutate(undefined, {
      onSuccess: (data) => {
        setTo(data.to ?? invoice.client?.email ?? '');
        setSubject(data.subject);
        setBody(data.body);
        setPopulated(true);
      },
      onError: () => {
        setTo(invoice.client?.email ?? '');
        setSubject('');
        setBody('');
        setPopulated(true);
        toast.warning(t('sendDialog.draftFailed'));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!to || !subject || !body || sendMutation.isPending) return;
    sendMutation.mutate(
      { to, subject, body },
      {
        onSuccess: () => {
          toast.success(
            mode === 'send'
              ? t('sendDialog.toast.sent')
              : t('sendDialog.toast.reminded'),
          );
          onOpenChange(false);
        },
        onError: () => {
          toast.error(t('sendDialog.toast.error'));
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && sendMutation.isPending) return;
    onOpenChange(next);
  };

  const isDrafting = draftMutation.isPending;
  const isSending = sendMutation.isPending;
  const canSubmit =
    populated && !!to && !!subject && !!body && !isSending && !isDrafting;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`sendDialog.title.${mode}`)}</DialogTitle>
          <DialogDescription>{t(`sendDialog.subtitle.${mode}`)}</DialogDescription>
        </DialogHeader>

        {isDrafting && !populated ? (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-sm">{t('sendDialog.drafting')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="send-to">{t('sendDialog.field.to')}</Label>
              <Input
                id="send-to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="send-subject">{t('sendDialog.field.subject')}</Label>
              <Input
                id="send-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="send-body">{t('sendDialog.field.body')}</Label>
              <Textarea
                id="send-body"
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSending}
              >
                {t('sendDialog.cancel')}
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {isSending ? t('sendDialog.sending') : t('sendDialog.send')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
